<template>
  <div class="conversation-item__media">
    <video-player
      v-if="message.showPlayer"
      class="mb-2"
      :item="{ values: { url: message.media, summary: {} } }"
      :active="true"
      :popup="false"
      :taking="false"
      :testing="false"
      :options="clientPlayerOptions"
    />

    <div
      v-else
      class="media-wrap"
      :class="{ 'media-wrap--video': isVideo }"
      @click="openMedia"
    >
      <template v-if="isVideo">
        <v-btn fab light>
          <v-icon color="appMain">play_arrow</v-icon>
        </v-btn>
        <img
          v-if="videoPreviewPath"
          v-lazy="videoPreviewPath"
          alt="Video Thumbnail"
        />

        <video v-else-if="mediaPath" :src="mediaPath" />
      </template>

      <template v-else-if="isImage">
        <template v-if="mediaPath">
          <img v-lazy="mediaPath" alt="Message Image" />
        </template>
      </template>

      <div v-else class="file-item">
        <v-icon class="file-item__icon" large color="primary">
          insert_drive_file
        </v-icon>
        <span class="file-item__main">
          {{ message.filename }}
        </span>
      </div>

      <v-btn
        fab
        x-small
        :href="downloadPath"
        :download="message.filename"
        target="_blank"
        color="appMain"
        class="media__download-link"
        @click.stop
      >
        <v-icon> file_download </v-icon>
      </v-btn>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import EventBus from '@/factories/eventBus';
import { openFullscreen } from '@/lib/misc';
import { isMobileDevice } from '@/filters/helpers';

import VideoPlayer from '@/views/lessons/lessonWidgets/videoWidget/VideoPlayer.vue';

export default {
  name: 'ConversationItemMedia',
  components: {
    VideoPlayer,
  },
  props: {
    message: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      clientPlayerOptions: {
        presentMode: true,
        autoPlay: true,
      },
    };
  },
  computed: {
    ...mapGetters(['cacheGet']),
    isVideo() {
      return this.checkType('video');
    },

    isImage() {
      return this.checkType('image');
    },

    videoPreviewPath() {
      return this.getMediaPath(this.message?.thumb_big);
    },

    mediaPath() {
      return this.getMediaPath(this.message?.media);
    },

    downloadPath() {
      if (this.mediaPath.includes('filestack')) {
        return `${this.mediaPath}?dl=true`;
      }

      return this.mediaPath;
    },
  },
  methods: {
    ...mapActions(['getTwilioMediaAuth', 'cacheSet']),

    getMediaPath(path) {
      if (!path || !path.startsWith('https://api.twilio.com')) {
        return path;
      }

      const signed = this.cacheGet(path);

      if (signed === 'signing') {
        return '';
      }

      if (signed) {
        return signed;
      }

      this.getMediaAuth(path);

      return '';
    },

    async getMediaAuth(path) {
      this.cacheSet([path, 'signing', 60]);
      const signedUrl = await this.getTwilioMediaAuth(path);
      this.cacheSet([path, signedUrl, 60]);
    },

    checkType(type) {
      const mimetype = this.message?.mimetype || '';
      return mimetype.includes(type);
    },

    showPlayer() {
      return this.isVideo && isMobileDevice();
    },

    openMedia() {
      const { media, video_id, security_token, mimetype } = this.message || {};

      let path = media;
      let signedPath = this.cacheGet(media);
      if (signedPath && signedPath !== 'signing') {
        path = signedPath;
      }

      if (video_id && security_token) {
        path = `https://videos.sproutvideo.com/embed/${video_id}/${security_token}`;
      }

      if (this.showPlayer(mimetype)) {
        this.initVideoPlayer(this.message);
        return;
      }

      EventBus.$emit('openChatModal', {
        modalType: 'MediaCard',
        data: {
          selectedMedia: {
            path,
            mimetype,
          },
        },
      });
    },

    initVideoPlayer(message) {
      this.$set(message, 'showPlayer', true);

      // waiting for player initialization
      setTimeout(() => {
        let container = document.getElementById(message.id);
        let video = container.querySelector('video');
        if (video) {
          openFullscreen(video);
          video.play();
        }
      }, 500);
    },
  },
};
</script>

<style lang="less">
.media-wrap {
  .file-item {
    background-color: var(--v-mainInvert-base) !important;
  }
  img {
    background-color: var(--v-darkGray-base);
  }
}

.conversation-item--mobile {
  .v-btn.media__download-link {
    opacity: 1;
  }
}

.conversation-item__media {
  display: flex;

  .media-wrap:hover {
    .v-btn.media__download-link {
      opacity: 1;
    }
  }
}

.v-btn.media__download-link {
  position: absolute;
  right: 6px;
  bottom: 6px;
  z-index: 1;
  opacity: 0;
  transition: all 0.3s ease;
}
</style>
